// /////////////////////////////////////////////////////////////
// [ファイル] SalesProjectFormFields.tsx
// [概要] 営業案件フォーム
// /////////////////////////////////////////////////////////////
import React, { useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Stack,
  InputAdornment,
  Autocomplete,
  Chip,
  Typography,
} from '@mui/material';
import * as Icons from "../constants/icons";
import { CATEGORY_LARGE, CATEGORY_MEDIUM_BY_LARGE, FIRE_INSURANCE_AMOUNT, FIRE_INSURANCE_PAYEE, PHASE, PROJECT_TYPE_OPTIONS } from '../constants/masterData';
import { useMasterData } from '../providers/MasterDataProvider';
import { isClientRequiredForPhase, hasValidClientId } from '../utils/validation';
import CrmCustomerForm from './CrmCustomerForm';
import LoadingView from './LoadingView';
import { ep } from '../api/endpoints';
import { sqmToTsubo } from '../utils/land';

const SEARCH_CUSTOMERS_URL = ep('search_crm_customers');
const SEARCH_CORPORATES_URL = ep('search_crm_corporates');
const SEARCH_PARENT_PROJECTS_URL = ep('search_sfa_purchase_projects');

interface SalesProjectFormFieldsProps {
  formData: any;
  onChange: (field: string, value: any) => void;
  currentTab: string;
  onTabChange: (tab: string) => void;
  hideTabs?: boolean;
  errors?: { [key: string]: string };
  viewLevel?: string;
}

// 入力項目の表示用: 上段にラベル(グレー)、下段に入力欄
const LabeledField: React.FC<{
  label: string;
  required?: boolean;
  extra?: React.ReactNode;
  children: React.ReactNode;
  sx?: any;
}> = ({ label, required, extra, children, sx }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ...sx }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography
        variant="caption"
        sx={{ color: 'grey.600', fontSize: '0.8rem', lineHeight: 1.4 }}
      >
        {label}
        {required && (
          <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
        )}
      </Typography>
      {extra}
    </Box>
    {children}
  </Box>
);

export default function SalesProjectFormFields({
  formData,
  onChange,
  currentTab,
  onTabChange,
  hideTabs = false,
  errors = {},
  viewLevel = 'individual',
}: SalesProjectFormFieldsProps) {
  const { staffList } = useMasterData();

  // 取引先（顧客）検索用ステート
  const [clientOptions, setClientOptions] = useState<any[]>([]);
  const [clientLoading, setClientLoading] = useState(false);
  // 取引先を「該当なし」からその場で新規作成するためのダイアログ開閉
  const [customerFormOpen, setCustomerFormOpen] = useState(false);
  // 新規顧客登録の API 実行中フラグ。ダイアログを閉じたあとに取引先欄をローディング表示へ差し替えるために使用。
  const [customerSaving, setCustomerSaving] = useState(false);

  // 親案件（仕入案件）検索用ステート。取引先検索と state を共有しない
  const [parentOptions, setParentOptions] = useState<any[]>([]);
  const [parentLoading, setParentLoading] = useState(false);
  const parentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const parentSeqRef = useRef(0);

  // 検索の debounce タイマーと最新リクエスト連番。
  // 連番でレスポンスの新旧を判定し、古いレスポンスが新しい結果を上書きするのを防ぐ。
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeqRef = useRef(0);

  // 取引先・不動産業者の検索を共通化。url を切り替えて使用する。
  const runSearch = (url: string, value: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!value) {
      setClientOptions([]);
      setClientLoading(false);
      return;
    }
    // debounce 待機中も「新規登録」項目が誤表示されないよう、即座にローディングへ。
    setClientLoading(true);
    const seq = ++searchSeqRef.current;
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(url, { query: value });
        if (seq !== searchSeqRef.current) return; // 古いレスポンスは破棄
        setClientOptions(res.data.status === 'success' ? (res.data.results || []) : []);
      } catch (err) {
        if (seq !== searchSeqRef.current) return;
        console.error('[Client/Corporate Search] Error:', err);
        setClientOptions([]);
      } finally {
        if (seq === searchSeqRef.current) setClientLoading(false);
      }
    }, 300);
  };

  const handleSearchClients = (value: string) => runSearch(SEARCH_CUSTOMERS_URL, value);

  const handleSearchCorporates = (value: string) => runSearch(SEARCH_CORPORATES_URL, value);

  // 親案件（仕入案件）の検索。取引先検索と同じ debounce + 連番パターンの専用版
  const handleSearchParentProjects = (value: string) => {
    if (parentTimerRef.current) clearTimeout(parentTimerRef.current);
    if (!value) {
      setParentOptions([]);
      setParentLoading(false);
      return;
    }
    setParentLoading(true);
    const seq = ++parentSeqRef.current;
    parentTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(SEARCH_PARENT_PROJECTS_URL, { query: value });
        if (seq !== parentSeqRef.current) return;
        setParentOptions(res.data.status === 'success' ? (res.data.results || []) : []);
      } catch (err) {
        if (seq !== parentSeqRef.current) return;
        console.error('[Parent Project Search] Error:', err);
        setParentOptions([]);
      } finally {
        if (seq === parentSeqRef.current) setParentLoading(false);
      }
    }, 300);
  };

  // /////////////////////////////////////////////////////////////
  // {} メソッド
  // /////////////////////////////////////////////////////////////
  const formatNumber = (val: any) => {
    if (val === undefined || val === null || val === '') return '';
    return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };


  const toHalfWidth = (str: string) => {
    return str.replace(/[！-～]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0))
      .replace(/　/g, " ");
  };

  const handleHalfWidthChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = toHalfWidth(e.target.value);
    const filteredVal = val.replace(/[^\x20-\x7e]/g, "");
    onChange(field, filteredVal);
  }; // 👆 ※正規変換を後から調査


  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(field, e.target.value);
  };

  // 大項目変更時、対応しない中項目はクリアする
  const handleCategoryLargeChange = (largeField: string, mediumField: string) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newLarge = e.target.value;
      onChange(largeField, newLarge);
      const validMediums = newLarge ? CATEGORY_MEDIUM_BY_LARGE[newLarge] ?? [] : [];
      const currentMedium = formData[mediumField];
      if (currentMedium && !validMediums.includes(currentMedium)) {
        onChange(mediumField, '');
      }
    };

  // 選択済み大項目に対応する中項目の選択肢を取得
  const getMediumOptions = (large: string): readonly string[] => {
    if (!large) return [];
    return CATEGORY_MEDIUM_BY_LARGE[large] ?? [];
  };

  // 桁区切り付き数値入力のカーソル位置復元用。
  // formatNumber のカンマ振り直しで value が差し替わるとカーソルが末尾へ飛ぶため、
  // 「カーソルより左にある数字の個数」を基準に再レンダリング後へ復元する。
  const caretRef = useRef<{ el: HTMLInputElement; digits: number } | null>(null);

  useLayoutEffect(() => {
    const pending = caretRef.current;
    caretRef.current = null;
    if (!pending) return;
    const { el, digits } = pending;
    // 先頭から数えて digits 個目の桁文字 (カンマ以外) の直後をカーソル位置とする
    const text = el.value;
    let seen = 0;
    let pos = text.length;
    for (let i = 0; i < text.length; i++) {
      if (seen >= digits) { pos = i; break; }
      if (text[i] !== ',') seen++;
    }
    if (seen < digits) pos = text.length;
    el.setSelectionRange(pos, pos);
  });

  // カーソルより左にある桁文字 (カンマ以外) の数を控える
  const rememberCaret = (el: HTMLInputElement) => {
    const caret = el.selectionStart ?? el.value.length;
    const digits = el.value.slice(0, caret).replace(/,/g, '').length;
    caretRef.current = { el, digits };
  };

  const handleNumberChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = e.target;
    // 全角数字は半角へ自動変換して受け付ける。
    // 拒否するだけだと state が更新されず DOM に全角文字が残りカーソルが乱れるため。
    const normalized = toHalfWidth(el.value);
    const rawValue = normalized.replace(/,/g, '');
    if (rawValue === '' || /^\d+$/.test(rawValue)) {
      rememberCaret(el);
      onChange(field, rawValue);
      return;
    }
    // 数字以外 (かな・記号など) は除去し、数字だけを残して入力を成立させる
    const digitsOnly = rawValue.replace(/\D/g, '');
    rememberCaret(el);
    onChange(field, digitsOnly);
  };


  // 小数を含む数値 (地積など) 用。整数部のみ 3 桁区切りし、小数部はそのまま保持する
  const formatDecimal = (val: any) => {
    if (val === undefined || val === null || val === '') return '';
    const [intPart, decPart] = val.toString().split('.');
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decPart !== undefined ? `${grouped}.${decPart}` : grouped;
  };

  // 小数 2 桁までの入力を許容する数値ハンドラ (入力途中の末尾「.」も許容)
  const handleDecimalChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = e.target;
    // 全角数字・全角ピリオドを半角へ寄せてから判定する
    const normalized = toHalfWidth(el.value).replace(/．/g, '.');
    const rawValue = normalized.replace(/,/g, '');
    if (rawValue === '' || /^\d*\.?\d{0,2}$/.test(rawValue)) {
      rememberCaret(el);
      onChange(field, rawValue);
      return;
    }
    // 数字と小数点以外を除去し、小数点は最初の 1 つ・小数部は 2 桁までに丸める
    const cleaned = rawValue.replace(/[^\d.]/g, '');
    const [intPart, ...rest] = cleaned.split('.');
    const next = rest.length > 0 ? `${intPart}.${rest.join('').slice(0, 2)}` : intPart;
    rememberCaret(el);
    onChange(field, next);
  };

  // 地積(平方メートル)から坪数を換算 (表示専用、DB へは保存しない)
  const tsubo = sqmToTsubo(formData.land_area);


  // /////////////////////////////////////////////////////////////
  // 🎨 セクション見出し
  // /////////////////////////////////////////////////////////////
  const SectionLabel = ({ label }: { label: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: -1 }}>
      <Box sx={{ width: 4, height: 18, bgcolor: 'primary.main', borderRadius: 1 }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{label}</Typography>
    </Box>
  );

  // 灰色塗り・角丸の統一スタイルを子フィールドへ流し込む（&& で詳細度を底上げ）
  const filledInputCascade = {
    '&& .MuiOutlinedInput-root': {
      backgroundColor: '#f5f7fa',
      borderRadius: 2,
    },
    '&& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '&& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0,0,0,0.12)',
    },
    '&& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'primary.main',
      borderWidth: 1,
    },
    '&& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: 'error.main',
    },
  };

  const isClientRequired = isClientRequiredForPhase(formData.phase);

  // /////////////////////////////////////////////////////////////
  // ⬅️ メインリターン
  // /////////////////////////////////////////////////////////////
  return (
    <Box sx={filledInputCascade}>
      {/* タブナビゲーション */}
      {!hideTabs && (
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => onTabChange(newValue)}
          indicatorColor="primary"
          textColor="primary"
          sx={{ mb: 3 }}
        >
          <Tab label="基本情報" value="basic" />
          <Tab label="土地" value="land" />
          <Tab label="建築" value="architecture" />
        </Tabs>
      )}

      {/* 基本情報 */}
      {currentTab === 'basic' && (
        <Box sx={{ p: 1 }}>
          <Stack spacing={3}>
            <SectionLabel label="案件情報" />
            <LabeledField label="案件名" required sx={{ width: '80%' }}>
              <TextField
                error={!!errors.project_name}
                helperText={errors.project_name}
                fullWidth
                value={formData.project_name || ''}
                onChange={handleChange('project_name')}
              />
            </LabeledField>

            <LabeledField label="フェーズ" sx={{ width: '30%' }}>
              <TextField
                select={!!formData.project_id}
                disabled={!formData.project_id || (viewLevel !== 'all' && viewLevel !== 'branch')}
                value={formData.phase || '1.見込み'}
                onChange={handleChange('phase')}
                fullWidth
                helperText={!formData.project_id ? "新規登録時は”見込み”が選択固定となります。" : ""}
              >
                {PHASE.map((val) => (
                  <MenuItem key={val} value={val}>{val}</MenuItem>
                ))}
              </TextField>
            </LabeledField>

            <LabeledField label="案件種別" required sx={{ width: '30%' }}>
              <TextField
                select
                error={!!errors.project_type}
                value={formData.project_type || ''}
                onChange={(e) => {
                  const newType = e.target.value || null;
                  onChange('project_type', newType);
                  // 売上案件以外は親案件を持てないためクリアする
                  if (newType !== 'sales') {
                    onChange('parent_project_id', null);
                    onChange('parent_project_name', '');
                  }
                }}
                fullWidth
                helperText={errors.project_type || ''}
              >
                {PROJECT_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.code} value={opt.code}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </LabeledField>

            {formData.project_type === 'sales' && (
              <LabeledField label="親案件（仕入）" sx={{ width: '50%' }}>
                <Autocomplete
                  options={parentOptions}
                  loading={parentLoading}
                  filterOptions={(x) => x} // サーバー側で絞り込むためクライアント側フィルタは無効化
                  getOptionLabel={(option: any) =>
                    typeof option === 'string' ? option : (option.project_name || '')
                  }
                  isOptionEqualToValue={(option: any, value: any) => option.project_id === value.project_id}
                  value={
                    formData.parent_project_id
                      ? { project_id: formData.parent_project_id, project_name: formData.parent_project_name || formData.parent_project_id }
                      : null
                  }
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'input') handleSearchParentProjects(newInputValue);
                  }}
                  onChange={(event, newValue: any) => {
                    onChange('parent_project_id', newValue ? newValue.project_id : null);
                    onChange('parent_project_name', newValue ? newValue.project_name : '');
                  }}
                  renderOption={(props, option: any) => {
                    const { key, ...optionProps } = props as any;
                    return (
                      <Box component="li" key={key} {...optionProps} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.project_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.project_id}
                          {option.client_name ? ` / ${option.client_name}` : ''}
                          {option.phase ? ` / ${option.phase}` : ''}
                        </Typography>
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!errors.parent_project_id}
                      helperText={errors.parent_project_id || '案件名・案件ID・取引先名・土地名で仕入案件を検索できます'}
                      placeholder="仕入案件を検索して選択"
                    />
                  )}
                  noOptionsText="該当する仕入案件がありません"
                />
              </LabeledField>
            )}

            <LabeledField
              label="取引先"
              required={isClientRequired}
              sx={{ width: '50%' }}
              extra={
                customerSaving ? null : formData.client_name ? (
                  hasValidClientId(formData.client_id) ? (
                    <Chip
                      icon={<Icons.CheckCircle sx={{ fontSize: 14 }} />}
                      label="顧客ID連携済"
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-icon': { ml: 0.5 } }}
                    />
                  ) : (
                    <Chip
                      icon={<Icons.HelpOutline sx={{ fontSize: 14 }} />}
                      label="未連携"
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-icon': { ml: 0.5 } }}
                    />
                  )
                ) : null
              }
            >
              {customerSaving ? (
                <LoadingView message="顧客を登録しています..." height="80px" size={30} />
              ) : (
              <Autocomplete
                freeSolo
                options={clientOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if ((option as any).isNew) return formData.client_name || '';
                  return option.display_name || '';
                }}
                filterOptions={(options) => {
                  // 検索結果0件かつ入力中の場合、Creatable パターンで「新規登録」項目を追加
                  if (formData.client_name && !clientLoading && options.length === 0) {
                    return [{ isNew: true, id: '__new__', display_name: formData.client_name }];
                  }
                  return options;
                }}
                loading={clientLoading}
                noOptionsText="顧客名を入力して検索してください"
                inputValue={formData.client_name || ''}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') {
                    onChange('client_name', value);
                    onChange('client_id', null);
                    handleSearchClients(value);
                  } else if (reason === 'clear') {
                    onChange('client_name', '');
                    onChange('client_id', null);
                  }
                }}
                onChange={(_, newValue) => {
                  if (newValue && typeof newValue === 'object') {
                    if ((newValue as any).isNew) {
                      // 「新規登録」項目を選択 → client_id/name は変えず、ダイアログを開く
                      setCustomerFormOpen(true);
                      return;
                    }
                    if (newValue.id) {
                      onChange('client_id', newValue.id);
                      onChange('client_name', newValue.display_name);
                    }
                  }
                }}
                renderOption={(props, option) => {
                  if ((option as any).isNew) {
                    return (
                      <li {...props} key="__new__">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                          <Icons.Add fontSize="small" />
                          <span>該当なし。「{formData.client_name}」を新規登録</span>
                        </Box>
                      </li>
                    );
                  }
                  return (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={option.customer_type === 'individual' ? '個人' : '法人'}
                          size="small"
                          variant="outlined"
                          color={option.customer_type === 'individual' ? 'success' : 'info'}
                        />
                        <span>{option.display_name}</span>
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="顧客名で検索"
                    error={!!errors.client_id}
                    helperText={
                      errors.client_id
                        ? errors.client_id
                        : (formData.client_name && !hasValidClientId(formData.client_id))
                          ? '顧客マスタから選び直してください（過去登録データは紐付けがありません）'
                          : isClientRequired
                            ? '顧客マスタから選択してください'
                            : '顧客マスタから選択してください（フェーズ2以降は必須）'
                    }
                  />
                )}
                sx={{ width: '100%' }}
              />
              )}
            </LabeledField>

            <SectionLabel label="担当者" />
            <Stack direction="row" spacing={2}>
              <LabeledField label="メイン担当者" sx={{ width: '25%' }}>
                <Autocomplete
                  options={staffList}
                  getOptionLabel={(option) => option.staff_name || ''}
                  value={staffList.find(s => s.staff_id === formData.main_staff_user_id) || null}
                  onChange={(event, newValue) => {
                    onChange('main_staff_user_id', newValue ? newValue.staff_id : '');
                    onChange('main_staff_name', newValue ? newValue.staff_name : '');
                    onChange('main_staff_department', newValue ? newValue.department : '');
                    onChange('main_staff_branch', newValue ? newValue.branch : '');
                  }}
                  isOptionEqualToValue={(option, value) => option.staff_id === value.staff_id}
                  renderOption={(props, option) => (
                    <li {...props} key={option.staff_id}>
                      {option.staff_name} ({option.branch_name})
                    </li>
                  )}
                  slotProps={{
                    popper: {
                      sx: { width: 'fit-content !important', minWidth: 240 },
                      placement: 'bottom-start',
                    },
                  }}
                  sx={{ width: '100%' }}
                  renderInput={(params) => (
                    <TextField {...params} />
                  )}
                />
              </LabeledField>

              <LabeledField label="案件所有者" sx={{ width: '25%' }}>
                <Autocomplete
                  options={staffList}
                  getOptionLabel={(option) => option.staff_name || ''}
                  value={staffList.find(s => s.staff_id === formData.owner_id) || null}
                  onChange={(event, newValue) => {
                    onChange('owner_id', newValue ? newValue.staff_id : '');
                    onChange('owner_name', newValue ? newValue.staff_name : '');
                    onChange('owner_department', newValue ? newValue.department : '');
                    onChange('owner_branch', newValue ? newValue.branch : '');
                  }}
                  isOptionEqualToValue={(option, value) => option.staff_id === value.staff_id}
                  renderOption={(props, option) => (
                    <li {...props} key={option.staff_id}>
                      {option.staff_name} ({option.branch_name})
                    </li>
                  )}
                  slotProps={{
                    popper: {
                      sx: { width: 'fit-content !important', minWidth: 240 },
                      placement: 'bottom-start',
                    },
                  }}
                  sx={{ width: '100%' }}
                  renderInput={(params) => (
                    <TextField {...params} />
                  )}
                />
              </LabeledField>

              <LabeledField label="担当者サポート" sx={{ width: '25%' }}>
                <Autocomplete
                  options={staffList}
                  getOptionLabel={(option) => option.staff_name || ''}
                  value={staffList.find(s => s.staff_id === formData.support_staff_id) || null}
                  onChange={(event, newValue) => {
                    onChange('support_staff_id', newValue ? newValue.staff_id : '');
                    onChange('support_staff_name', newValue ? newValue.staff_name : '');
                    onChange('support_staff_department', newValue ? newValue.department : '');
                    onChange('support_staff_branch', newValue ? newValue.branch : '');
                  }}
                  isOptionEqualToValue={(option, value) => option.staff_id === value.staff_id}
                  renderOption={(props, option) => (
                    <li {...props} key={option.staff_id}>
                      {option.staff_name} ({option.branch_name})
                    </li>
                  )}
                  slotProps={{
                    popper: {
                      sx: { width: 'fit-content !important', minWidth: 240 },
                      placement: 'bottom-start',
                    },
                  }}
                  sx={{ width: '100%' }}
                  renderInput={(params) => (
                    <TextField {...params} />
                  )}
                />
              </LabeledField>
            </Stack>

            <SectionLabel label="分類" />
            <LabeledField label="案件: レコードタイプ" sx={{ width: '30%' }}>
              <TextField
                select
                value={formData.record_type || ''}
                onChange={handleChange('record_type')}
                fullWidth
              >
                <MenuItem value="建物1">建物1</MenuItem>
                <MenuItem value="建物2">建物2</MenuItem>
                <MenuItem value="建物3">建物3</MenuItem>
                <MenuItem value="建物4">建物4</MenuItem>
                <MenuItem value="建物5">建物5</MenuItem>
                <MenuItem value="木造営業">木造営業</MenuItem>
                <MenuItem value="改修">改修</MenuItem>
                <MenuItem value="土地売り">土地売り</MenuItem>
                <MenuItem value="建主">建主</MenuItem>
                <MenuItem value="仲介">仲介</MenuItem>
                <MenuItem value="その他">その他</MenuItem>
              </TextField>
            </LabeledField>

            <Stack direction="row" spacing={2}>
              <LabeledField label="大項目" sx={{ width: '30%' }}>
                <TextField
                  select
                  value={formData.category_large || ''}
                  onChange={handleCategoryLargeChange('category_large', 'category_medium')}
                  fullWidth
                >
                  <MenuItem value="">選択してください</MenuItem>
                  {CATEGORY_LARGE.map((val) => (
                    <MenuItem key={val} value={val}>{val}</MenuItem>
                  ))}
                  {!!formData.category_large && !CATEGORY_LARGE.includes(formData.category_large as any) && (
                    <MenuItem key={formData.category_large} value={formData.category_large}>{formData.category_large} (旧)</MenuItem>
                  )}
                </TextField>
              </LabeledField>
              <LabeledField label="大項目_予備" sx={{ width: '30%' }}>
                <TextField
                  select
                  value={formData.category_large_extra || ''}
                  onChange={handleCategoryLargeChange('category_large_extra', 'category_medium_extra')}
                  fullWidth
                >
                  <MenuItem value="">選択してください</MenuItem>
                  {CATEGORY_LARGE.map((val) => (
                    <MenuItem key={val} value={val}>{val}</MenuItem>
                  ))}
                  {!!formData.category_large_extra && !CATEGORY_LARGE.includes(formData.category_large_extra as any) && (
                    <MenuItem key={formData.category_large_extra} value={formData.category_large_extra}>{formData.category_large_extra} (旧)</MenuItem>
                  )}
                </TextField>
              </LabeledField>
            </Stack>

            <Stack direction="row" spacing={2}>
              <LabeledField label="中項目" sx={{ width: '30%' }}>
                <TextField
                  select
                  value={formData.category_medium || ''}
                  onChange={handleChange('category_medium')}
                  disabled={!formData.category_large}
                  helperText={!formData.category_large ? '先に大項目を選択してください' : ''}
                  fullWidth
                >
                  <MenuItem value="">選択してください</MenuItem>
                  {getMediumOptions(formData.category_large).map((val) => (
                    <MenuItem key={val} value={val}>{val}</MenuItem>
                  ))}
                  {!!formData.category_medium && !getMediumOptions(formData.category_large).includes(formData.category_medium) && (
                    <MenuItem key={formData.category_medium} value={formData.category_medium}>{formData.category_medium} (旧)</MenuItem>
                  )}
                </TextField>
              </LabeledField>
              <LabeledField label="中項目_予備" sx={{ width: '30%' }}>
                <TextField
                  select
                  value={formData.category_medium_extra || ''}
                  onChange={handleChange('category_medium_extra')}
                  disabled={!formData.category_large_extra}
                  helperText={!formData.category_large_extra ? '先に大項目_予備を選択してください' : ''}
                  fullWidth
                >
                  <MenuItem value="">選択してください</MenuItem>
                  {getMediumOptions(formData.category_large_extra).map((val) => (
                    <MenuItem key={val} value={val}>{val}</MenuItem>
                  ))}
                  {!!formData.category_medium_extra && !getMediumOptions(formData.category_large_extra).includes(formData.category_medium_extra) && (
                    <MenuItem key={formData.category_medium_extra} value={formData.category_medium_extra}>{formData.category_medium_extra} (旧)</MenuItem>
                  )}
                </TextField>
              </LabeledField>
            </Stack>

            <SectionLabel label="受注情報" />
            <Stack direction="row" spacing={2}>
              <LabeledField label="アポイント取得方法" sx={{ width: '25%' }}>
                <TextField
                  select
                  value={formData.appointment_method || ''}
                  onChange={handleChange('appointment_method')}
                  fullWidth
                >
                  <MenuItem value="①顧客訪問">①顧客訪問</MenuItem>
                  <MenuItem value="②電話集客">②電話集客</MenuItem>
                  <MenuItem value="③反響">③反響</MenuItem>
                  <MenuItem value="④紹介">④紹介</MenuItem>
                  <MenuItem value="⑤その他">⑤その他</MenuItem>
                </TextField>
              </LabeledField>
              <LabeledField label="案件化手法" sx={{ width: '25%' }}>
                <TextField
                  select
                  value={formData.lead_generation_method || ''}
                  onChange={handleChange('lead_generation_method')}
                  fullWidth
                >
                  <MenuItem value="①現調">①現調</MenuItem>
                  <MenuItem value="②現調(不在地主)">②現調(不在地主)</MenuItem>
                  <MenuItem value="③相続登記リスト">③相続登記リスト</MenuItem>
                  <MenuItem value="④その他リスト">④その他リスト</MenuItem>
                  <MenuItem value="⑤仲介業者">⑤仲介業者</MenuItem>
                  <MenuItem value="⑥反響">⑥反響</MenuItem>
                  <MenuItem value="⑦紹介（知人）">⑦紹介（知人）</MenuItem>
                  <MenuItem value="⑧紹介（業者）">⑧紹介（業者）</MenuItem>
                  <MenuItem value="⑨その他">⑨その他</MenuItem>
                </TextField>
              </LabeledField>
            </Stack>

            <LabeledField
              label={formData.project_type === 'purchase' ? '決済日' : '受注確定日'}
              sx={{ width: '18%' }}
            >
              <TextField
                type="date"
                value={formData.order_confirmed_date || ''}
                onChange={handleChange('order_confirmed_date')}
                fullWidth
                error={!!errors.order_confirmed_date}
                helperText={errors.order_confirmed_date}
              />
            </LabeledField>

            <LabeledField label="工事案件番号" sx={{ width: '25%' }}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={Array.isArray(formData.new_system_number) ? formData.new_system_number : []}
                onChange={(_, newValue) => {
                  // 確定時に各要素を半角変換し、空要素を除外して配列で保持
                  const cleaned = (newValue as string[])
                    .map((v) => toHalfWidth(v).replace(/[^\x20-\x7e]/g, "").trim())
                    .filter((v) => v !== "");
                  onChange('new_system_number', cleaned);
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip label={option} size="small" key={key} {...tagProps} />;
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    helperText="半角英数字で入力し、Enter で追加してください"
                  />
                )}
                sx={{ width: '100%' }}
              />
            </LabeledField>

            <Stack direction="row" spacing={2} sx={{ width: '80%' }}>
              <LabeledField label="中長期追客予定" sx={{ width: '18%' }}>
                <TextField
                  select
                  value={formData.long_term_follow_up === 1 ? 1 : 0}
                  onChange={(e) => onChange('long_term_follow_up', Number(e.target.value))}
                  fullWidth
                >
                  <MenuItem value={0}>通常</MenuItem>
                  <MenuItem value={1}>中長期</MenuItem>
                </TextField>
              </LabeledField>

              <LabeledField label={formData.project_type === 'purchase' ? '契約予定年' : '受注予定年'} sx={{ width: '15%' }}>
                <TextField
                  placeholder="YYYY"
                  value={formData.expected_order_year || ''}
                  onChange={handleNumberChange('expected_order_year')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">年</InputAdornment>,
                  }}
                  inputProps={{
                    style: { textAlign: 'right' },
                    maxLength: 4
                  }}
                  fullWidth
                />
              </LabeledField>

              <LabeledField label={formData.project_type === 'purchase' ? '契約予定月' : '受注予定月'} sx={{ width: '13%' }}>
                <TextField
                  select
                  value={formData.expected_order_month || ''}
                  onChange={(e) => onChange('expected_order_month', e.target.value)}
                  fullWidth
                >
                  <MenuItem value="">選択</MenuItem>
                  {[...Array(12)].map((_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>{i + 1}月</MenuItem>
                  ))}
                </TextField>
              </LabeledField>

              <LabeledField label={formData.project_type === 'purchase' ? '契約金額' : '売上'} sx={{ width: '18%' }}>
                <TextField
                  value={formatNumber(formData.order_sales_amount)}
                  onChange={handleNumberChange('order_sales_amount')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">円</InputAdornment>,
                  }}
                  inputProps={{
                    style: { textAlign: 'right' }
                  }}
                  fullWidth
                />
              </LabeledField>
            </Stack>

            <SectionLabel label="取引業者・金融機関" />
            <LabeledField 
              label="不動産業者(管理・仲介)" 
              sx={{ width: '40%' }}
              extra={
                formData.real_estate_agency_mgmt ? (
                  hasValidClientId(formData.real_estate_agency_mgmt_id) ? (
                    <Chip
                      icon={<Icons.CheckCircle sx={{ fontSize: '1rem !important' }} />}
                      label="顧客ID連携済"
                      size="small"
                      color="success"
                      sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-icon': { ml: 0.5 } }}
                    />
                  ) : (
                    <Chip
                      label="未連携"
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-icon': { ml: 0.5 } }}
                    />
                  )
                ) : null
              }
            >
              <Autocomplete
                freeSolo
                options={clientOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.display_name || option.company_name || '';
                }}
                filterOptions={(x) => x}
                loading={clientLoading}
                noOptionsText="業者名を入力して検索してください"
                inputValue={formData.real_estate_agency_mgmt || ''}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') {
                    onChange('real_estate_agency_mgmt', value);
                    onChange('real_estate_agency_mgmt_id', null);
                    handleSearchCorporates(value);
                  } else if (reason === 'clear') {
                    onChange('real_estate_agency_mgmt', '');
                    onChange('real_estate_agency_mgmt_id', null);
                  }
                }}
                onChange={(_, newValue) => {
                  if (newValue && typeof newValue === 'object' && newValue.id) {
                    onChange('real_estate_agency_mgmt_id', newValue.id);
                    onChange('real_estate_agency_mgmt', newValue.display_name || newValue.company_name);
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={option.customer_type === 'individual' ? '個人' : '法人'}
                        size="small"
                        variant="outlined"
                        color={option.customer_type === 'individual' ? 'success' : 'info'}
                      />
                      <span>{option.display_name || option.company_name}</span>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="顧客マスタから検索"
                    error={!!errors.real_estate_agency_mgmt_id}
                    helperText={
                      errors.real_estate_agency_mgmt_id
                        ? errors.real_estate_agency_mgmt_id
                        : (formData.real_estate_agency_mgmt && !hasValidClientId(formData.real_estate_agency_mgmt_id))
                          ? '顧客マスタから選び直してください（過去登録データは紐付けがありません）'
                          : '顧客マスタから選択してください'
                    }
                  />
                )}
                sx={{ width: '100%' }}
              />
            </LabeledField>
            <LabeledField 
              label="不動産業者(売却仲介)" 
              sx={{ width: '40%' }}
              extra={
                formData.real_estate_agency_brokerage ? (
                  hasValidClientId(formData.real_estate_agency_brokerage_id) ? (
                    <Chip
                      icon={<Icons.CheckCircle sx={{ fontSize: '1rem !important' }} />}
                      label="顧客ID連携済"
                      size="small"
                      color="success"
                      sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-icon': { ml: 0.5 } }}
                    />
                  ) : (
                    <Chip
                      label="未連携"
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-icon': { ml: 0.5 } }}
                    />
                  )
                ) : null
              }
            >
              <Autocomplete
                freeSolo
                options={clientOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.display_name || option.company_name || '';
                }}
                filterOptions={(x) => x}
                loading={clientLoading}
                noOptionsText="業者名を入力して検索してください"
                inputValue={formData.real_estate_agency_brokerage || ''}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') {
                    onChange('real_estate_agency_brokerage', value);
                    onChange('real_estate_agency_brokerage_id', null);
                    handleSearchCorporates(value);
                  } else if (reason === 'clear') {
                    onChange('real_estate_agency_brokerage', '');
                    onChange('real_estate_agency_brokerage_id', null);
                  }
                }}
                onChange={(_, newValue) => {
                  if (newValue && typeof newValue === 'object' && newValue.id) {
                    onChange('real_estate_agency_brokerage_id', newValue.id);
                    onChange('real_estate_agency_brokerage', newValue.display_name || newValue.company_name);
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={option.customer_type === 'individual' ? '個人' : '法人'}
                        size="small"
                        variant="outlined"
                        color={option.customer_type === 'individual' ? 'success' : 'info'}
                      />
                      <span>{option.display_name || option.company_name}</span>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="顧客マスタから検索"
                    error={!!errors.real_estate_agency_brokerage_id}
                    helperText={
                      errors.real_estate_agency_brokerage_id
                        ? errors.real_estate_agency_brokerage_id
                        : (formData.real_estate_agency_brokerage && !hasValidClientId(formData.real_estate_agency_brokerage_id))
                          ? '顧客マスタから選び直してください（過去登録データは紐付けがありません）'
                          : '顧客マスタから選択してください'
                    }
                  />
                )}
                sx={{ width: '100%' }}
              />
            </LabeledField>
            <LabeledField label="不動産取得税" sx={{ width: '40%' }}>
              <TextField
                value={formatNumber(formData.real_estate_acquisition_tax)}
                onChange={handleNumberChange('real_estate_acquisition_tax')}
                InputProps={{
                  endAdornment: <InputAdornment position="end">円</InputAdornment>,
                }}
                inputProps={{ style: { textAlign: 'right' } }}
                fullWidth
              />
            </LabeledField>
            <LabeledField label="固都税" sx={{ width: '40%' }}>
              <TextField
                value={formatNumber(formData.fixed_asset_city_tax)}
                onChange={handleNumberChange('fixed_asset_city_tax')}
                InputProps={{
                  endAdornment: <InputAdornment position="end">円</InputAdornment>,
                }}
                inputProps={{ style: { textAlign: 'right' } }}
                fullWidth
              />
            </LabeledField>
            {/* 火災保険は支払先・金額とも全社一律の固定契約のため入力不可 */}
            <Stack direction="row" spacing={2} sx={{ width: '80%' }}>
              <LabeledField label="火災保険 (支払業者)" sx={{ width: '50%' }}>
                <TextField
                  value={FIRE_INSURANCE_PAYEE}
                  disabled
                  fullWidth
                  helperText="支払業者は固定です"
                />
              </LabeledField>
              <LabeledField label="火災保険 (金額)" sx={{ width: '31%' }}>
                <TextField
                  value={formatNumber(FIRE_INSURANCE_AMOUNT)}
                  disabled
                  InputProps={{
                    endAdornment: <InputAdornment position="end">円</InputAdornment>,
                  }}
                  inputProps={{ style: { textAlign: 'right' } }}
                  fullWidth
                  helperText="金額は一律固定です"
                />
              </LabeledField>
            </Stack>
            <Stack direction="row" spacing={2} sx={{ width: '80%' }}>
              <LabeledField label="所有権移転費用 (士業支払先)" sx={{ width: '50%' }}>
                <TextField
                  value={formData.ownership_transfer_payee || ''}
                  onChange={handleChange('ownership_transfer_payee')}
                  fullWidth
                />
              </LabeledField>
              <LabeledField label="所有権移転費用 (金額)" sx={{ width: '31%' }}>
                <TextField
                  value={formatNumber(formData.ownership_transfer_cost)}
                  onChange={handleNumberChange('ownership_transfer_cost')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">円</InputAdornment>,
                  }}
                  inputProps={{ style: { textAlign: 'right' } }}
                  fullWidth
                />
              </LabeledField>
            </Stack>
            <LabeledField
              label={
                formData.project_type === 'purchase'
                  ? '仕入時仲介手数料'
                  : formData.project_type === 'sales'
                    ? '売却時仲介手数料'
                    : '仲介手数料'
              }
              sx={{ width: '40%' }}
            >
              <TextField
                value={formatNumber(formData.brokerage_fee)}
                onChange={handleNumberChange('brokerage_fee')}
                InputProps={{
                  endAdornment: <InputAdornment position="end">円</InputAdornment>,
                }}
                inputProps={{ style: { textAlign: 'right' } }}
                fullWidth
              />
            </LabeledField>
            <LabeledField label="金融機関" sx={{ width: '40%' }}>
              <TextField
                value={formData.financial_institution || ''}
                onChange={handleChange('financial_institution')}
                fullWidth
              />
            </LabeledField>

            <SectionLabel label="メモ" />
            <LabeledField label="問題点" sx={{ width: '80%' }}>
              <TextField
                fullWidth
                multiline
                rows={5}
                value={formData.issues || ''}
                onChange={handleChange('issues')}
              />
            </LabeledField>

            <LabeledField label="備考" sx={{ width: '80%' }}>
              <TextField
                fullWidth
                multiline
                rows={5}
                value={formData.remarks || ''}
                onChange={handleChange('remarks')}
              />
            </LabeledField>
          </Stack>
        </Box>
      )}

      {/* 土地情報 */}
      {currentTab === 'land' && (
        <Box sx={{ p: 1 }}>
          <Stack spacing={3}>
            <LabeledField label="土地名称" sx={{ width: '70%' }}>
              <TextField
                value={formData.land_name || ''}
                onChange={handleChange('land_name')}
                fullWidth
              />
            </LabeledField>
            <LabeledField label="敷地面積 (㎡)" sx={{ width: '20%' }}>
              <TextField
                fullWidth
                value={formatDecimal(formData.land_area)}
                onChange={handleDecimalChange('land_area')}
                helperText={tsubo != null ? `約 ${tsubo.toFixed(2)} 坪` : ' '}
                InputProps={{
                  endAdornment: <InputAdornment position="end">㎡</InputAdornment>,
                }}
                inputProps={{
                  style: { textAlign: 'right' }
                }}
              />
            </LabeledField>
            {/* 分筆・合筆 (元の登記筆数 → 移転登記後の筆数、数値のみ。合筆・分筆なしは 1 → 1、仕入案件のみ表示) */}
            {formData.project_type === 'purchase' && (
            <Stack direction="row" spacing={2} alignItems="flex-end">
              <LabeledField label="元の登記筆数" sx={{ width: '15%' }}>
                <TextField
                  fullWidth
                  value={formatNumber(formData.parcel_count_before)}
                  onChange={handleNumberChange('parcel_count_before')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">筆</InputAdornment>,
                  }}
                  inputProps={{
                    style: { textAlign: 'right' }
                  }}
                />
              </LabeledField>
              <Typography sx={{ color: 'grey.600', pb: 2 }}>→</Typography>
              <LabeledField label="移転登記後の筆数" sx={{ width: '15%' }}>
                <TextField
                  fullWidth
                  value={formatNumber(formData.parcel_count_after)}
                  onChange={handleNumberChange('parcel_count_after')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">筆</InputAdornment>,
                  }}
                  inputProps={{
                    style: { textAlign: 'right' }
                  }}
                />
              </LabeledField>
            </Stack>
            )}
            {/* 解体・造成・給排水・確定測量は金額入力方式 (0円以上の入力あり = あり扱い) */}
            <Stack direction="row" spacing={2}>
              <LabeledField label="解体" sx={{ width: '18%' }}>
                <TextField
                  fullWidth
                  value={formatNumber(formData.demolition_cost)}
                  onChange={handleNumberChange('demolition_cost')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">円</InputAdornment>,
                  }}
                  inputProps={{
                    style: { textAlign: 'right' }
                  }}
                />
              </LabeledField>
              <LabeledField label="造成" sx={{ width: '18%' }}>
                <TextField
                  fullWidth
                  value={formatNumber(formData.land_prep_cost)}
                  onChange={handleNumberChange('land_prep_cost')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">円</InputAdornment>,
                  }}
                  inputProps={{
                    style: { textAlign: 'right' }
                  }}
                />
              </LabeledField>
              <LabeledField label="給排水" sx={{ width: '18%' }}>
                <TextField
                  fullWidth
                  value={formatNumber(formData.water_supply_cost)}
                  onChange={handleNumberChange('water_supply_cost')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">円</InputAdornment>,
                  }}
                  inputProps={{
                    style: { textAlign: 'right' }
                  }}
                />
              </LabeledField>
              <LabeledField label="確定測量" sx={{ width: '18%' }}>
                <TextField
                  fullWidth
                  value={formatNumber(formData.land_survey_cost)}
                  onChange={handleNumberChange('land_survey_cost')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">円</InputAdornment>,
                  }}
                  inputProps={{
                    style: { textAlign: 'right' }
                  }}
                />
              </LabeledField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <LabeledField label="坪単価(想定)" sx={{ width: '15%' }}>
                <TextField
                  fullWidth
                  value={formatNumber(formData.estimated_price_per_tsubo)}
                  onChange={handleNumberChange('estimated_price_per_tsubo')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">円</InputAdornment>,
                  }}
                  inputProps={{
                    style: { textAlign: 'right' }
                  }}
                />
              </LabeledField>
              <LabeledField label="坪単価(希望)" sx={{ width: '15%' }}>
                <TextField
                  fullWidth
                  value={formatNumber(formData.desired_price_per_tsubo)}
                  onChange={handleNumberChange('desired_price_per_tsubo')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">円</InputAdornment>,
                  }}
                  inputProps={{
                    style: { textAlign: 'right' }
                  }}
                />
              </LabeledField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <LabeledField label="想定買取可能額" sx={{ width: '15%' }}>
                <TextField
                  fullWidth
                  value={formatNumber(formData.estimated_purchase_price)}
                  onChange={handleNumberChange('estimated_purchase_price')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">円</InputAdornment>,
                  }}
                  inputProps={{
                    style: { textAlign: 'right' }
                  }}
                />
              </LabeledField>
              <LabeledField label="売却希望額" sx={{ width: '15%' }}>
                <TextField
                  fullWidth
                  value={formatNumber(formData.desired_selling_price)}
                  onChange={handleNumberChange('desired_selling_price')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">円</InputAdornment>,
                  }}
                  inputProps={{
                    style: { textAlign: 'right' }
                  }}
                />
              </LabeledField>
            </Stack>
          </Stack>
        </Box>
      )}

      {/* 建築 */}
      {currentTab === 'architecture' && (
        <Box sx={{ p: 1 }}>
          <Stack spacing={3}>
            <Stack direction="row" spacing={2}>
              <LabeledField label="建物１" sx={{ width: '40%' }}>
                <TextField
                  value={formData.building_1 || ''}
                  onChange={handleChange('building_1')}
                  fullWidth
                />
              </LabeledField>
              <LabeledField label="建物２" sx={{ width: '40%' }}>
                <TextField
                  value={formData.building_2 || ''}
                  onChange={handleChange('building_2')}
                  fullWidth
                />
              </LabeledField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <LabeledField label="建物３" sx={{ width: '40%' }}>
                <TextField
                  value={formData.building_3 || ''}
                  onChange={handleChange('building_3')}
                  fullWidth
                />
              </LabeledField>
              <LabeledField label="建物４" sx={{ width: '40%' }}>
                <TextField
                  value={formData.building_4 || ''}
                  onChange={handleChange('building_4')}
                  fullWidth
                />
              </LabeledField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <LabeledField label="建築費種類" sx={{ width: '40%' }}>
                <TextField
                  value={formData.building_cost_type || ''}
                  onChange={handleChange('building_cost_type')}
                  fullWidth
                />
              </LabeledField>
              <LabeledField label="想定 工事費合計" sx={{ width: '20%' }}>
                <TextField
                  fullWidth
                  value={formatNumber(formData.estimated_total_construction_cost)}
                  onChange={handleNumberChange('estimated_total_construction_cost')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">円</InputAdornment>,
                  }}
                  inputProps={{
                    style: { textAlign: 'right' }
                  }}
                />
              </LabeledField>
            </Stack>
          </Stack>
        </Box>
      )}

      {/* 取引先「該当なし」からの新規顧客登録ダイアログ
          customerFormOpen が false でも customerSaving 中はマウントを保持し、
          バックグラウンド処理（axios リクエストや内部 Snackbar）を継続させる。 */}
      {(customerFormOpen || customerSaving) && (
        <CrmCustomerForm
          open={customerFormOpen}
          onClose={() => setCustomerFormOpen(false)}
          onSaveStart={() => {
            // 「保存する」押下と同時にダイアログを閉じ、取引先欄をローディングへ差し替える
            setCustomerFormOpen(false);
            setCustomerSaving(true);
          }}
          onSaveError={() => {
            // 失敗時はローディングを解除しダイアログを再表示してリトライさせる
            setCustomerSaving(false);
            setCustomerFormOpen(true);
          }}
          onSuccess={(newId, newDisplayName) => {
            onChange('client_id', newId);
            if (newDisplayName) onChange('client_name', newDisplayName);
            setClientOptions([]);
            setCustomerSaving(false);
            setCustomerFormOpen(false);
          }}
        />
      )}

    </Box>
  );
}
